import { useMemo } from "react";
import { useModuleBEM, useUiStyles } from "@b2bportal/core-themes";
import {
  CoreUiComponents,
  type PlatformComponentProps,
} from "@b2bportal/core-types";
import { useI18nContext } from "@hopper-b2b/i18n";
import clsx from "clsx";
import TripAdvisorRating00 from "./assets/tripadvisor-0-0.svg?react";
import TripAdvisorRating05 from "./assets/tripadvisor-0-5.svg?react";
import TripAdvisorRating10 from "./assets/tripadvisor-1-0.svg?react";
import TripAdvisorRating15 from "./assets/tripadvisor-1-5.svg?react";
import TripAdvisorRating20 from "./assets/tripadvisor-2-0.svg?react";
import TripAdvisorRating25 from "./assets/tripadvisor-2-5.svg?react";
import TripAdvisorRating30 from "./assets/tripadvisor-3-0.svg?react";
import TripAdvisorRating35 from "./assets/tripadvisor-3-5.svg?react";
import TripAdvisorRating40 from "./assets/tripadvisor-4-0.svg?react";
import TripAdvisorRating45 from "./assets/tripadvisor-4-5.svg?react";
import TripAdvisorRating50 from "./assets/tripadvisor-5-0.svg?react";
import defaultStyles from "./TripadvisorRatingIcon.module.scss";

export type TripadvisorRatingIconComponentProps = {
  scoreOutOfFive: number;
} & PlatformComponentProps;

const COMPONENT_KEY = CoreUiComponents.TripadvisorRatingIcon;
export const TripadvisorRatingIcon = ({
  scoreOutOfFive,
  className,
}: TripadvisorRatingIconComponentProps) => {
  const styles = useUiStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);
  const { t } = useI18nContext();
  const roundedScore = Math.round(scoreOutOfFive * 2) / 2;
  const scoreOneDecimalPlace = roundedScore.toFixed(1);
  const TripadvisorBadge = useMemo(() => {
    switch (scoreOneDecimalPlace) {
      case "0.5":
        return TripAdvisorRating05;
      case "1.0":
        return TripAdvisorRating10;
      case "1.5":
        return TripAdvisorRating15;
      case "2.0":
        return TripAdvisorRating20;
      case "2.5":
        return TripAdvisorRating25;
      case "3.0":
        return TripAdvisorRating30;
      case "3.5":
        return TripAdvisorRating35;
      case "4.0":
        return TripAdvisorRating40;
      case "4.5":
        return TripAdvisorRating45;
      case "5.0":
        return TripAdvisorRating50;
      default:
        return TripAdvisorRating00;
    }
  }, [scoreOneDecimalPlace]);

  return (
    <div
      className={clsx(block, className)}
      role="img"
      aria-label={t("tripAdvisorBadge", { scoreOneDecimalPlace })}
    >
      <TripadvisorBadge className={cn("image")} aria-hidden="true" />
    </div>
  );
};
